import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { filter, map, pairwise, startWith } from 'rxjs';
import { MarkdownPipe } from '@medinbox/angular';
import { LivekitService } from '../../core/services/livekit.service';
import { PromptBox } from './prompt-box/prompt-box';
import { ChatAIthinkingComponent } from './chat-thinking';
import { MibLogo } from '../mib-header/mib-logo';
import { CopyrightAndLegalLink } from '../copyright-and-legal-link';

@Component({
  selector: 'app-chat',
  imports: [MarkdownPipe, PromptBox, ChatAIthinkingComponent, MibLogo, CopyrightAndLegalLink],
  templateUrl: './chat.html',
  styleUrl: './chat.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Chat implements OnInit {
  private readonly livekit = inject(LivekitService);

  readonly chatContainerRef = viewChild<ElementRef>('chatContainer');
  readonly promptBoxRef = viewChild.required(PromptBox);

  readonly isPttActive = signal(false);
  readonly hidePtt = computed(() => this.livekit.currentRole() === 'audience');
  readonly messages = this.livekit.messages;
  /** Whether the conversation already contains at least one message. */
  readonly hasMessages = computed(() => this.messages().length > 0);
  readonly isThinking = this.livekit.isThinking;
  readonly localIdentity = this.livekit.localParticipantIdentity;
  readonly chatSpacerHeight = signal(0);
  readonly pttTranscription = this.livekit.pttTranscription;

  private readonly finalPTT = toObservable(this.livekit.pttFinalTranscription);

  /** Guards the one-off instant jump to the bottom on the very first message load. */
  private firstLoadDone = false;

  constructor() {
    // Auto-scroll is driven reactively from the messages signal (single source of
    // truth) rather than from imperative calls, so both user and agent messages
    // trigger it. We compare consecutive list lengths to know whether the list grew.
    toObservable(this.messages)
      .pipe(
        map((entries) => entries.length),
        startWith(0),
        pairwise(),
        takeUntilDestroyed(),
      )
      .subscribe(([prev, curr]) => this.handleMessagesChange(prev, curr));
  }

  ngOnInit(): void {
    this.finalPTT.pipe(filter((text) => !!text)).subscribe((text) => {
      this.promptBoxRef().setPrompt(text);
    });
  }

  sendMessage(text: string): void {
    // The reactive subscription above scrolls once the message lands in the list.
    this.livekit.sendMessage(text);
  }

  /**
   * Reacts to a change in the number of messages and scrolls accordingly.
   * Wrapped in `setTimeout(0)` so the measurement happens after Angular has
   * rendered the new message (otherwise the scroll lags one message behind).
   */
  private handleMessagesChange(prev: number, curr: number): void {
    if (curr === 0) return;
    setTimeout(() => {
      if (!this.firstLoadDone) {
        // First population (e.g. restored history) → jump straight to the latest.
        this.firstLoadDone = true;
        this.scrollToBottom('instant');
        return;
      }
      if (curr <= prev) return; // shrink/refresh → keep the current position
      // Align the top of the new message with the top of the viewport. This keeps
      // the user's question pinned at the top while the answer loads, and — crucially
      // for long agent replies — always reveals the *start* of the message instead of
      // scrolling to the bottom (which would cut off the top).
      this.scrollLastMessageToTop();
    });
  }

  private scrollToBottom(behavior: ScrollBehavior): void {
    const container = this.chatContainerRef()?.nativeElement;
    if (!container) return;
    container.scrollTo({ top: container.scrollHeight, behavior });
  }

  private scrollLastMessageToTop(): void {
    const container = this.chatContainerRef()?.nativeElement;
    if (!container) return;
    const messages = container.querySelectorAll('.markdown-message');
    const lastMessage = messages[messages.length - 1] as HTMLElement | undefined;
    if (!lastMessage) return;

    // Anti-jitter: do nothing if the target is already at the top of the viewport.
    if (this.isElementAtTop(lastMessage, container)) return;

    // Add enough space below so the last message can reach the top of the container.
    this.chatSpacerHeight.set(Math.max(0, container.clientHeight / 2));

    // Wait for the spacer to render, then scroll.
    setTimeout(() => lastMessage.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  }

  /** True when `el` is already aligned with the top of `container` (within 8px). */
  private isElementAtTop(el: HTMLElement, container: HTMLElement): boolean {
    const elTop = el.getBoundingClientRect().top;
    const containerTop = container.getBoundingClientRect().top;
    return Math.abs(elTop - containerTop) < 8;
  }

  onPttStart(): void {
    this.isPttActive.set(true);
    this.livekit.startPTT();
  }

  onPttStop(): void {
    this.isPttActive.set(false);
    this.livekit.stopPTT();
  }
}
